import api from "../../services/Api";
import "./parametros.css";
// src/components/Configuraciones/EditarParametros.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import configuracionesService from "../../services/configuracionesService";
import { isSuperAdmin } from "../../services/authUtils";

import { usePermisos } from "../../context/PermisosContext";

/* ================== Helpers ================== */
const normalizarPermisos = (p) => {
  if (Array.isArray(p)) return p.map(String);
  if (typeof p === "string" && p.trim().length) {
    return p
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .map(String);
  }
  return [];
};

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.usuarios)) return data.usuarios;
  return [];
};

const emptyNuevo = () => ({
  tipoParametroId: "",
  unidad: "",
  optimoMin: "",
  optimoMax: "",
  aceptableMin: "",
  aceptableMax: "",
  criticoMin: "",
  criticoMax: "",
  permisos: [],
});

const EditarParametros = ({ onBack }) => {
  const { can, canEdit } = usePermisos();
  const puedeVer = can("CONFIGURACIONES_VIEW") && can("CONFIGURACIONES_TAB_PARAMETROS_VIEW") && can("CONFIGURACIONES_PARAMETROS_LISTA_VIEW");
  const [generales, setGenerales] = useState(false);
  const [nombreTipo, setNombreTipo] = useState("");
  const esSuper = isSuperAdmin();
  const puedeEditar = canEdit("CONFIGURACIONES_EDIT") && canEdit("CONFIGURACIONES_TAB_PARAMETROS_EDIT") && can("CONFIGURACIONES_PARAMETRO_EDITAR_VIEW") && canEdit("CONFIGURACIONES_PARAMETRO_EDITAR_EDIT");

  const puedeCrear = puedeEditar && canEdit("CONFIGURACIONES_PARAMETROS_LISTA_EDIT");

  // ======= state/hook siempre en top-level =======
  const [tipos, setTipos] = useState([]);
  
  const [items, setItems] = useState([]);
  const [snapshots, setSnapshots] = useState({});
  const [nuevo, setNuevo] = useState(emptyNuevo());

  const [onlyActivos, setOnlyActivos] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [flash, setFlash] = useState({});
  const [loading, setLoading] = useState(false);

  // Listado rápido: imprimir/exportar
  const columnsDef = [
    { key: "tipo", label: "Tipo" },
    { key: "unidad", label: "Unidad" },
    { key: "optMin", label: "Óptimo Min" },
    { key: "optMax", label: "Óptimo Max" },
    { key: "acepMin", label: "Aceptable Min" },
    { key: "acepMax", label: "Aceptable Max" },
    { key: "criMin", label: "Crítico Min" },
    { key: "criMax", label: "Crítico Max" },
    { key: "activo", label: "Activo" },

  ];
  const [showPrint, setShowPrint] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  const [colsToPrint, setColsToPrint] = useState({
    tipo: true,
    unidad: true,
    optMin: true,
    optMax: true,
    acepMin: true,
    acepMax: true,
    criMin: true,
    criMax: true,
    activo: true,

  });
  const [colsToExport, setColsToExport] = useState({
    tipo: true,
    unidad: true,
    optMin: true,
    optMax: true,
    acepMin: true,
    acepMax: true,
    criMin: true,
    criMax: true,
    activo: true,

  });

  const mountedRef = useRef(true);
  const requestRef = useRef(0);

  const activos = useMemo(() => items.filter((i) => i.estaActivo), [items]);

  const visibles = useMemo(() => {
    const base = onlyActivos ? activos : items;
    const q = busqueda.trim().toLowerCase();
    if (!q) return base;
    return base.filter((r) => {
      const fields = [
        r.tipoParametroNombre || "",
        r.unidad || "",
        (r.optimoMin ?? "").toString(),
        (r.optimoMax ?? "").toString(),
        (r.aceptableMin ?? "").toString(),
        (r.aceptableMax ?? "").toString(),
        (r.criticoMin ?? "").toString(),
        (r.criticoMax ?? "").toString(),
      ]
        .join("|")
        .toLowerCase();
      return fields.includes(q);
    });
  }, [items, activos, onlyActivos, busqueda]);

  const showFlash = (key, { ok, err }) => {
    setFlash((prev) => ({ ...prev, [key]: { ok, err } }));
    setTimeout(() => {
      setFlash((prev) => {
        const c = { ...prev };
        delete c[key];
        return c;
      });
    }, 3000);
  };

  const cargarTodo = async () => {
    if (!puedeVer) return;
    const ticket = ++requestRef.current;
    setLoading(true);
    try {
      const [lista, tiposResponse] = await Promise.all([
        configuracionesService.listarValoresReferencia(true, generales),
        api.get("/TiposParametros", { params: { generales } }),
      ]);
      if (!mountedRef.current || ticket !== requestRef.current) return;
      const tiposData = tiposResponse.data;
      const arr = toArray(lista);
      setItems(arr);
      const snap = {};
      arr.forEach((x) => (snap[x.id] = JSON.parse(JSON.stringify(x))));
      setSnapshots(snap);
      setTipos(toArray(tiposData));

      setError("");
    } catch (e) {
      console.error(e);
      if (ticket === requestRef.current) setError("Error cargando datos.");
    } finally {
      if (mountedRef.current && ticket === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    cargarTodo();
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVer, generales]);

  if (!puedeVer) {
    return (
      <div className="p-6 bg-neutral-900 text-white rounded-lg shadow-md">
        <h3 className="text-red-400 text-xl font-semibold">
          Sin permisos para ver Configuraciones.
        </h3>
      </div>
    );
  }

  /* ========== Handlers básicos ========== */
  const handleNuevo = (campo, valor) =>
    setNuevo((n) => ({ ...n, [campo]: valor }));

  const handleChange = (id, campo, valor) =>
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, [campo]: valor } : x)));

  const revertir = (id) => {
    const snap = snapshots[id];
    if (!snap) return;
    setItems((prev) =>
      prev.map((x) => (x.id === id ? JSON.parse(JSON.stringify(snap)) : x))
    );
    showFlash(id, { ok: "Revertido." });
  };

  /* ========== CRUD ========== */
  const crear = async () => {
    if (!puedeCrear) return;
    try {
      const payload = {
        tipoParametroId: Number(nuevo.tipoParametroId),
        unidad: (nuevo.unidad || "").trim(),
        optimoMin: nuevo.optimoMin === "" ? null : Number(nuevo.optimoMin),
        optimoMax: nuevo.optimoMax === "" ? null : Number(nuevo.optimoMax),
        aceptableMin: nuevo.aceptableMin === "" ? null : Number(nuevo.aceptableMin),
        aceptableMax: nuevo.aceptableMax === "" ? null : Number(nuevo.aceptableMax),
        criticoMin: nuevo.criticoMin === "" ? null : Number(nuevo.criticoMin),
        criticoMax: nuevo.criticoMax === "" ? null : Number(nuevo.criticoMax),
        permisos: normalizarPermisos(nuevo.permisos),
      };
      const resp = await configuracionesService.crearValorReferencia(payload, generales);
      showFlash("nuevo", { ok: "Creado correctamente." });
      await cargarTodo();
      setNuevo(emptyNuevo());
      setMensaje(resp?.mensaje || "Creado");
      setError("");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) showFlash("nuevo", { err: "Ya existe un valor para ese tipo." });
      else showFlash("nuevo", { err: "No se pudo crear." });
      setError("Error creando registro.");
      console.error(e);
    }
  };

  const guardar = async (it) => {
    if (!puedeEditar) return;
    try {
      const payload = {
        tipoParametroId: Number(it.tipoParametroId),
        unidad: (it.unidad || "").trim(),
        optimoMin: it.optimoMin === "" ? null : Number(it.optimoMin),
        optimoMax: it.optimoMax === "" ? null : Number(it.optimoMax),
        aceptableMin: it.aceptableMin === "" ? null : Number(it.aceptableMin),
        aceptableMax: it.aceptableMax === "" ? null : Number(it.aceptableMax),
        criticoMin: it.criticoMin === "" ? null : Number(it.criticoMin),
        criticoMax: it.criticoMax === "" ? null : Number(it.criticoMax),
        estaActivo: !!it.estaActivo,
        permisos: normalizarPermisos(it.permisos),
      };
      if (generales) {
        const {data: impacto} = await api.get(`/Configuraciones/valores-referencia/${it.id}/impacto`);
        if (!window.confirm(`${impacto.mensaje}\nSensores: ${impacto.sensores}\n¿Aplicar?`)) return;
      }
      await configuracionesService.actualizarValorReferencia(it.id, payload, generales);
      await cargarTodo();
      showFlash(it.id, { ok: "Guardado." });
      setMensaje("Actualizado correctamente.");
      setError("");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) showFlash(it.id, { err: "Tipo duplicado." });
      else showFlash(it.id, { err: "Error al guardar." });
      setError("Error al actualizar.");
      console.error(e);
    }
  };

  const toggleActivo = async (it, activo) => {
    if (!puedeEditar) return;
    try {
      if (activo) await configuracionesService.reactivarValorReferencia(it.id, generales);
      else await configuracionesService.desactivarValorReferencia(it.id, generales);
      await cargarTodo();
      showFlash(it.id, { ok: activo ? "Reactivado." : "Desactivado." });
    } catch (e) {
      showFlash(it.id, { err: "No se pudo cambiar estado." });
      console.error(e);
    }
  };

  const restablecer = async (it) => {
    if (!puedeEditar || !window.confirm("¿Volver a los valores generales vigentes? También recibirás sus futuras actualizaciones.")) return;
    try {
      await api.put(`/Configuraciones/valores-referencia/restablecer/${it.tipoParametroId}`);
      await cargarTodo(); setMensaje("Valores predeterminados restablecidos.");
    } catch { setError("No se pudieron restablecer los valores."); }
  };
  const crearTipo = async () => {
    if (!puedeCrear) return;
    try {
      const {data} = await api.post("/TiposParametros", {nombre: nombreTipo}, {params: {generales}});
      await cargarTodo(); setNuevo(n => ({...n, tipoParametroId: String(data.id)})); setNombreTipo("");
      setMensaje("Parámetro creado. Completa sus unidades y rangos.");
    } catch (e) { setError(typeof e.response?.data === "string" ? e.response.data : "No se pudo crear el parámetro."); }
  };

  /* ========== Imprimir / Exportar ========== */
  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const escapeCsv = (val) => {
    const s = String(val ?? "");
    const needsQuotes = /[",\r\n;]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const buildTheadHtml = (cols) =>
    `<tr>${cols.map((c) => `<th>${c.label}</th>`).join("")}</tr>`;

  const buildRowTdsHtml = (r, colsMap) => {
    const tds = [];
    if (colsMap.tipo) tds.push(`<td>${escapeHtml(r.tipoParametroNombre || "")}</td>`);
    if (colsMap.unidad) tds.push(`<td>${escapeHtml(r.unidad || "")}</td>`);
    if (colsMap.optMin) tds.push(`<td style="text-align:right">${escapeHtml(r.optimoMin ?? "")}</td>`);
    if (colsMap.optMax) tds.push(`<td style="text-align:right">${escapeHtml(r.optimoMax ?? "")}</td>`);
    if (colsMap.acepMin) tds.push(`<td style="text-align:right">${escapeHtml(r.aceptableMin ?? "")}</td>`);
    if (colsMap.acepMax) tds.push(`<td style="text-align:right">${escapeHtml(r.aceptableMax ?? "")}</td>`);
    if (colsMap.criMin) tds.push(`<td style="text-align:right">${escapeHtml(r.criticoMin ?? "")}</td>`);
    if (colsMap.criMax) tds.push(`<td style="text-align:right">${escapeHtml(r.criticoMax ?? "")}</td>`);
    if (colsMap.activo) tds.push(`<td style="text-align:center">${r.estaActivo ? "Sí" : "No"}</td>`);
    if (colsMap.perms) tds.push(`<td style="text-align:center">${normalizarPermisos(r.permisos).length}</td>`);
    return tds.join("");
  };

  const buildTableHtml = (colsMap) => {
    const cols = columnsDef.filter((c) => colsMap[c.key]);
    const thead = buildTheadHtml(cols);
    const htmlRows = visibles.map((r) => `<tr>${buildRowTdsHtml(r, colsMap)}</tr>`).join("");
    return { thead, htmlRows };
  };

  const buildPrintHtml = (colsMap, title = "Valores de Referencia") => {
    const { thead, htmlRows } = buildTableHtml(colsMap);
    const now = new Date().toLocaleString();
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @media print { @page { size: auto; margin: 12mm; } }
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color:#111827; }
  h1 { font-size:20px; margin:0 0 4px 0; }
  .sub { color:#6b7280; font-size:12px; margin-bottom:12px; }
  table { border-collapse: collapse; width: 100%; font-size:12px; }
  th, td { border:1px solid #0ea5a3; padding:8px; vertical-align: top; }
  thead th { background:#111827; color:#67e8f9; }
  tr:nth-child(even) td { background:#f8fafc; }
</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="sub">Generado: ${escapeHtml(now)} • Total: ${visibles.length}</div>
  <table>
    <thead>${thead}</thead>
    <tbody>${htmlRows}</tbody>
  </table>
  <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 300); };</script>
</body>
</html>`;
  };

  const handlePrint = () => {
    const html = buildPrintHtml(colsToPrint, "Valores de Referencia");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCsv = () => {
    const headers = columnsDef.filter((h) => colsToExport[h.key]);
    const headerRow = headers.map((h) => escapeCsv(h.label)).join(",");
    const dataRows = visibles.map((r) => {
      const vals = [];
      if (colsToExport.tipo) vals.push(escapeCsv(r.tipoParametroNombre || ""));
      if (colsToExport.unidad) vals.push(escapeCsv(r.unidad || ""));
      if (colsToExport.optMin) vals.push(escapeCsv(r.optimoMin ?? ""));
      if (colsToExport.optMax) vals.push(escapeCsv(r.optimoMax ?? ""));
      if (colsToExport.acepMin) vals.push(escapeCsv(r.aceptableMin ?? ""));
      if (colsToExport.acepMax) vals.push(escapeCsv(r.aceptableMax ?? ""));
      if (colsToExport.criMin) vals.push(escapeCsv(r.criticoMin ?? ""));
      if (colsToExport.criMax) vals.push(escapeCsv(r.criticoMax ?? ""));
      if (colsToExport.activo) vals.push(escapeCsv(r.estaActivo ? "Sí" : "No"));
      if (colsToExport.perms)
        vals.push(escapeCsv(String(normalizarPermisos(r.permisos).length)));
      return vals.join(",");
    });
    const csv = [headerRow, ...dataRows].join("\r\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const iso = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    triggerDownload(url, `valores-referencia-${iso}.csv`);
  };

  const exportXls = () => {
    const { thead, htmlRows } = buildTableHtml(colsToExport);
    const now = new Date().toLocaleString();
    const title = "Valores de Referencia";
    const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>${escapeHtml(title)}</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
  <table border="1" style="border-collapse:collapse;">
    <thead style="background:#111827;color:#67e8f9;font-weight:bold;">${thead}</thead>
    <tbody>${htmlRows}</tbody>
  </table>
  <div>Generado: ${escapeHtml(now)} • Total: ${visibles.length}</div>
</body>
</html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const iso = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    triggerDownload(url, `valores-referencia-${iso}.xls`);
  };

  const exportPdf = () => {
    const html = buildPrintHtml(colsToExport, "Valores de Referencia");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const handleExport = () => {
    if (exportFormat === "csv") return exportCsv();
    if (exportFormat === "xls") return exportXls();
    if (exportFormat === "pdf") return exportPdf();
  };

  /* ========== Render ========== */
  const bloqueNuevo = (
    <div className="border border-cyan-500 rounded p-4 mb-6 bg-neutral-800">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-cyan-400 text-lg">Crear valores de referencia</h4>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block mb-1">Tipo de Parámetro</label>
          <select
            value={nuevo.tipoParametroId}
            onChange={(e) => handleNuevo("tipoParametroId", e.target.value)}
            className="w-full p-2 rounded bg-neutral-700 text-white"
          >
            <option value="">Seleccione</option>
            {tipos.map((tp) => (
              <option key={tp.id} value={tp.id}>
                {tp.nombre}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1">Unidad</label>
          <input
            value={nuevo.unidad}
            onChange={(e) => handleNuevo("unidad", e.target.value)}
            className="w-full p-2 rounded bg-neutral-700 text-white"
            placeholder="Ej. °C, pH, mg/L"
          />
        </div>
        <div className="hidden md:block"></div>

        {[
          ["optimoMin", "Óptimo Min"],
          ["optimoMax", "Óptimo Max"],
          ["aceptableMin", "Aceptable Min"],
          ["aceptableMax", "Aceptable Max"],
          ["criticoMin", "Crítico Min"],
          ["criticoMax", "Crítico Max"],
        ].map(([k, label]) => (
          <div key={k}>
            <label className="block mb-1">{label}</label>
            <input
              type="number"
              step="0.01"
              value={nuevo[k]}
              onChange={(e) => handleNuevo(k, e.target.value)}
              className="w-full p-2 rounded bg-neutral-700 text-white"
            />
          </div>
        ))}


      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          disabled={!puedeCrear}
          onClick={crear}
          className={`px-4 py-2 rounded ${
            puedeCrear
              ? "bg-green-600 hover:bg-green-700"
              : "bg-neutral-600 cursor-not-allowed"
          } text-white`}
        >
          Crear
        </button>
        {flash["nuevo"]?.ok && (
          <span className="text-green-400 text-sm">{flash["nuevo"].ok}</span>
        )}
        {flash["nuevo"]?.err && (
          <span className="text-red-400 text-sm">{flash["nuevo"].err}</span>
        )}
      </div>
    </div>
  );

  const tarjetaItem = (it) => (
    <div
      key={it.id}
      className={`border rounded p-4 mb-6 ${
        it.estaActivo
          ? "border-cyan-500 bg-neutral-800"
          : "border-neutral-600 bg-neutral-900 opacity-80"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-cyan-400 text-lg">
          {it.tipoParametroNombre || "(Tipo)"} — {generales ? "General" : it.personalizado ? "Personalizado" : "Predeterminado"}
        </h4>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block mb-1">Tipo de Parámetro</label>
          <select
            disabled value={it.tipoParametroId || ""}
            onChange={(e) =>
              handleChange(it.id, "tipoParametroId", e.target.value)
            }
            className="w-full p-2 rounded bg-neutral-700 text-white"
          >
            <option value="">Seleccione</option>
            {tipos.map((tp) => (
              <option key={tp.id} value={tp.id}>
                {tp.nombre}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1">Unidad</label>
          <input
            value={it.unidad || ""}
            onChange={(e) => handleChange(it.id, "unidad", e.target.value)}
            className="w-full p-2 rounded bg-neutral-700 text-white"
          />
        </div>
        <div>
          <label className="block mb-1">Activo</label>
          <select
            value={it.estaActivo ? "true" : "false"}
            onChange={(e) =>
              handleChange(it.id, "estaActivo", e.target.value === "true")
            }
            className="w-full p-2 rounded bg-neutral-700 text-white"
          >
            <option value="true">Activo</option>
            <option value="false">Inactivo</option>
          </select>
        </div>

        {[
          ["optimoMin", "Óptimo Min"],
          ["optimoMax", "Óptimo Max"],
          ["aceptableMin", "Aceptable Min"],
          ["aceptableMax", "Aceptable Max"],
          ["criticoMin", "Crítico Min"],
          ["criticoMax", "Crítico Max"],
        ].map(([k, label]) => (
          <div key={k}>
            <label className="block mb-1">{label}</label>
            <input
              type="number"
              step="0.01"
              value={it[k] ?? ""}
              onChange={(e) => handleChange(it.id, k, e.target.value)}
              className="w-full p-2 rounded bg-neutral-700 text-white"
            />
          </div>
        ))}


      </div>

      <div className="parameter-actions mt-4">
        <button
          disabled={!puedeEditar}
          onClick={() => guardar(it)}
          className={`px-4 py-2 rounded ${
            puedeEditar
              ? "bg-green-600 hover:bg-green-700"
              : "bg-neutral-600 cursor-not-allowed"
          } text-white`}
        >
          Guardar cambios
        </button>

        {it.estaActivo ? (
          <button
            disabled={!puedeEditar}
            onClick={() => toggleActivo(it, false)}
            className={`px-4 py-2 rounded ${
              puedeEditar
                ? "bg-yellow-600 hover:bg-yellow-700"
                : "bg-neutral-600 cursor-not-allowed"
            } text-white`}
          >
            Desactivar
          </button>
        ) : (
          <button
            disabled={!puedeEditar}
            onClick={() => toggleActivo(it, true)}
            className={`px-4 py-2 rounded ${
              puedeEditar
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-neutral-600 cursor-not-allowed"
            } text-white`}
          >
            Reactivar
          </button>
        )}

        {!generales && it.personalizado && it.tienePredeterminado && <button disabled={!puedeEditar} onClick={() => restablecer(it)} className="min-h-12 px-4 rounded bg-cyan-700">Restablecer predeterminados</button>}
        <button onClick={() => revertir(it.id)} className="min-h-12 px-4 rounded bg-slate-700">Descartar cambios</button>

        {flash[it.id]?.ok && (
          <span className="text-green-400 text-sm">{flash[it.id].ok}</span>
        )}
        {flash[it.id]?.err && (
          <span className="text-red-400 text-sm">{flash[it.id].err}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="parameter-editor min-w-0 p-2 sm:p-6 bg-neutral-900 text-white rounded-lg shadow-md">
      {/* Header + acciones */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          {typeof onBack === "function" && (
            <button
              onClick={onBack}
              className="bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded text-sm"
            >
              ← Regresar
            </button>
          )}
          <h3 className="text-green-400 text-2xl font-semibold">
            Rangos y alertas
          </h3>
        </div>

        <div className="parameter-actions">
          <button
            onClick={() => setOnlyActivos(true)}
            className={`px-3 py-2 rounded text-sm ${
              onlyActivos ? "bg-emerald-700" : "bg-slate-700 hover:bg-slate-600"
            }`}
          >
            Ver activos
          </button>
          <button
            onClick={() => setOnlyActivos(false)}
            className={`px-3 py-2 rounded text-sm ${
              !onlyActivos ? "bg-emerald-700" : "bg-slate-700 hover:bg-slate-600"
            }`}
          >
            Ver todos
          </button>
          <button
            onClick={cargarTodo}
            disabled={loading}
            className="px-3 py-2 rounded text-sm bg-cyan-700 hover:bg-cyan-600 disabled:opacity-60"
          >
            {loading ? "Actualizando…" : "Refrescar"}
          </button>
        </div>
      </div>

      <div className="my-4 p-4 rounded bg-slate-800 space-y-3">
        {esSuper && <label>Ámbito de edición <select aria-label="Ámbito de edición" value={generales ? "general" : "cuenta"} onChange={e => {setGenerales(e.target.value === "general");setItems([]);setNuevo(emptyNuevo());}} className="p-3 bg-slate-700 rounded"><option value="cuenta">Mi cuenta</option><option value="general">Valores generales del sistema</option></select></label>}
        <p>{generales ? "Los cambios generales llegan únicamente a las cuentas que usan los predeterminados." : "Estos parámetros pertenecen a tu cuenta. Tus personalizaciones y parámetros nuevos no cambian los de otras cuentas. Los usuarios temporales autorizados usan los de la cuenta propietaria."}</p>
        <p>Óptimo, aceptable (subóptimo) y crítico se aplican a las próximas lecturas. Desactivar los rangos detiene su evaluación en alarmas y automatizaciones.</p>

      </div>

      {mensaje && <p className="text-green-400 mt-2">{mensaje}</p>}
      {error && <p className="text-red-400 mt-2">{error}</p>}

      {/* ===== Listado rápido ===== */}
      <div className="mt-4 rounded border border-slate-800">
        <div className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-slate-800">
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por tipo, unidad o rango…"
            className="flex-1 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-600"
          />
          <div className="parameter-actions">
            <button
              onClick={() => setShowPrint((s) => !s)}
              className="bg-cyan-700 hover:bg-cyan-600 text-white px-3 py-2 rounded text-sm"
            >
              Imprimir
            </button>
            <button
              onClick={() => setShowExport((s) => !s)}
              className="bg-emerald-700 hover:bg-emerald-600 text-white px-3 py-2 rounded text-sm"
            >
              Exportar
            </button>
          </div>
        </div>

        {showPrint && (
          <div className="p-3 border-t border-slate-700">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Opciones de impresión</h4>
              <button
                onClick={handlePrint}
                className="bg-cyan-700 hover:bg-cyan-600 text-white px-3 py-1 rounded text-sm"
              >
                Imprimir ahora
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              {Object.entries(colsToPrint).map(([k, v]) => (
                <label key={k} className="flex items-center gap-2">
                  <input type="checkbox" checked={v} onChange={() => setColsToPrint((p) => ({ ...p, [k]: !p[k] }))} />
                  <span>{columnsDef.find((c) => c.key === k)?.label ?? k}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {showExport && (
          <div className="p-3 border-t border-slate-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm">Formato:</span>
                <select
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
                >
                  <option value="csv">CSV</option>
                  <option value="xls">Excel (HTML)</option>
                  <option value="pdf">PDF (imprimir)</option>
                </select>
              </div>
              <button
                onClick={handleExport}
                className="bg-emerald-700 hover:bg-emerald-600 text-white px-3 py-1 rounded text-sm"
              >
                Exportar ahora
              </button>
            </div>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              {Object.entries(colsToExport).map(([k, v]) => (
                <label key={k} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={v}
                    onChange={() => setColsToExport((p) => ({ ...p, [k]: !p[k] }))}
                  />
                  <span>{columnsDef.find((c) => c.key === k)?.label ?? k}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-cyan-200">
              <tr>
                <th className="px-2 py-2 text-left">Tipo</th>
                <th className="px-2 py-2 text-left">Unidad</th>
                <th className="px-2 py-2 text-right">Ópt. Min</th>
                <th className="px-2 py-2 text-right">Ópt. Max</th>
                <th className="px-2 py-2 text-right">Acept. Min</th>
                <th className="px-2 py-2 text-right">Acept. Max</th>
                <th className="px-2 py-2 text-right">Crít. Min</th>
                <th className="px-2 py-2 text-right">Crít. Max</th>
                <th className="px-2 py-2 text-center">Activo</th>
                
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="px-3 py-4 text-center text-gray-300">
                    Cargando…
                  </td>
                </tr>
              )}
              {!loading && visibles.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-4 text-center text-gray-400">
                    Sin resultados.
                  </td>
                </tr>
              )}
              {!loading &&
                visibles.map((r) => (
                  <tr key={r.id} className="odd:bg-slate-900 even:bg-slate-950">
                    <td className="px-2 py-2">{r.tipoParametroNombre}</td>
                    <td className="px-2 py-2">{r.unidad}</td>
                    <td className="px-2 py-2 text-right">{r.optimoMin ?? ""}</td>
                    <td className="px-2 py-2 text-right">{r.optimoMax ?? ""}</td>
                    <td className="px-2 py-2 text-right">{r.aceptableMin ?? ""}</td>
                    <td className="px-2 py-2 text-right">{r.aceptableMax ?? ""}</td>
                    <td className="px-2 py-2 text-right">{r.criticoMin ?? ""}</td>
                    <td className="px-2 py-2 text-right">{r.criticoMax ?? ""}</td>
                    <td className="px-2 py-2 text-center">{r.estaActivo ? "Sí" : "No"}</td>
                    
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== Crear nuevo ===== */}
      {puedeCrear && <details className="my-4 rounded border border-slate-700 p-3"><summary className="cursor-pointer py-3 font-semibold text-cyan-300">Agregar un parámetro o configurar rangos nuevos</summary><div className="space-y-4 mt-3">        {puedeCrear && <div className="flex flex-col sm:flex-row gap-2"><input aria-label="Nombre del nuevo parámetro" maxLength={50} value={nombreTipo} onChange={e => setNombreTipo(e.target.value)} placeholder="Nombre de un parámetro nuevo" className="min-w-0 p-3 bg-slate-700 rounded"/><button disabled={!nombreTipo.trim()} onClick={crearTipo} className="min-h-12 p-3 bg-cyan-700 rounded">Crear parámetro {generales ? "general" : "privado"}</button></div>}{bloqueNuevo}</div></details>}

      {/* ===== Edición ===== */}
      {(onlyActivos ? activos : items).map(tarjetaItem)}
    </div>
  );
};

export default EditarParametros;
